import React from "react";
import {
  Container,
  FilterContainer,
  FilterSection,
  PeriodFilter,
  Section,
  Skeleton,
} from "./ViolationReservations.styles";
import UI from "@mile-meeting/mile-ui";
import { getKorDate } from "utils/date";
import { TSearchPeriod, TViolationRsvListItem } from "./types.d";
import { useEffect, useRef, useState } from "react";
import CalendarFilter from "./uis/calendar";
import ViolationRsvListTable from "./uis/table";
import { ViolationRsvListServices } from "./service";
import { useToast } from "hook/useToast";
import { AmplitudeService } from "utils/amplitude.service";
import { useRecoilValue } from "recoil";
import { workspace } from "recoil/recoilState";

type ViolationReservationsProps = {
  isShow: boolean;
};
const ViolationReservations: React.FC<ViolationReservationsProps> = ({ isShow }) => {
  const afterMonthAgo = new Date();
  const amplitudeService = new AmplitudeService();
  const workspaceInfo = useRecoilValue(workspace);
  afterMonthAgo.setMonth(afterMonthAgo.getMonth() + 1);
  const [period, setPeriod] = useState<TSearchPeriod>({
    startDate: new Date(),
    endDate: afterMonthAgo,
  }); //* INFO : 조회 기간

  const { showToast } = useToast();
  const calendarRef = useRef<HTMLDivElement>(null);
  const [openCalendarModal, setOpenCalendarModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [violationRsvList, setViolationRsvList] = useState<TViolationRsvListItem[]>([]);
  const services = new ViolationRsvListServices();

  //* INFO : 최소 참석 인원 위반 예약 조회
  const setViolationReservationList = async (startDate: Date, endDate: Date, page: number) => {
    setLoading(true);

    services.getViolationRsvList(startDate, endDate, page).then((data) => {
      amplitudeService.logMinAttendanceViolationReservationQuery(
        workspaceInfo?.id ?? "",
        workspaceInfo?.name ?? "",
        period.startDate,
        period.endDate,
        page
      );
      if (data.success) {
        setViolationRsvList(data.list ?? []);
        setPage(page);
        setTotalPage(data.totalPageNumber);
      }

      setLoading(false);
    });
  };

  const isDateDifferenceExceedingMax = (start: Date, end: Date, maxDurationInMonths: number): boolean => {
    const yearDifference = end.getFullYear() - start.getFullYear();
    const monthDifference = end.getMonth() - start.getMonth();

    const totalMonthsDifference = yearDifference * 12 + monthDifference;

    return totalMonthsDifference > maxDurationInMonths;
  };

  useEffect(() => {
    setViolationReservationList(period.startDate, period.endDate, page);
  }, [page]);

  useEffect(() => {
    setViolationReservationList(period.startDate, period.endDate, page);
  }, [period]);

  useEffect(() => {
    const handler = (event: any) => {
      if (openCalendarModal && calendarRef.current && !calendarRef.current.contains(event.target)) {
        setOpenCalendarModal?.(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, [openCalendarModal]);

  return !isShow ? (
    <></>
  ) : (
    <Container>
      <FilterSection>
        <FilterContainer>
          <PeriodFilter ref={calendarRef} className="period-filter">
            <UI.Typography type="body01 normal" fontStyle="medium" color="var(--alias-color-label-normal)">
              조회 기간
            </UI.Typography>
            <div
              onFocus={(e) => {
                setOpenCalendarModal(true);
              }}
            >
              <UI.InputGroup.TextField
                value={getKorDate(period.startDate.toDateString()).replaceAll(".", ". ")}
                size="small"
                prefixIcon="calendar"
                showPrefixIcon
                disabledFocus
              />
            </div>
            <div style={{ width: 12, height: 1, backgroundColor: "var(--alias-color-label-normal)" }} />
            <div
              onFocus={() => {
                setOpenCalendarModal(true);
              }}
            >
              <UI.InputGroup.TextField
                value={getKorDate(period.endDate.toDateString()).replaceAll(".", ". ")}
                size="small"
                prefixIcon="calendar"
                showPrefixIcon
                disabledFocus
              />
            </div>
            <CalendarFilter
              selectedDate={[period.startDate, period.endDate]}
              open={openCalendarModal}
              onChange={(startDate, endDate) => {
                if (startDate && endDate) {
                  if (isDateDifferenceExceedingMax(startDate, endDate, 6)) {
                    showToast("최대 조회 가능 기간을 초과했습니다. 기간을 다시 선택해 주세요.", "warning");
                    setPeriod({ startDate: new Date(), endDate: afterMonthAgo });
                  } else {
                    setPeriod({ startDate, endDate });
                  }
                }
              }}
            />
          </PeriodFilter>
        </FilterContainer>
        <UI.Typography
          style={{ padding: "0 20px" }}
          type="label01"
          fontStyle="medium"
          color="var(--alias-color-label-alternative)"
        >
          * 최대 조회 가능 기간은 6개월입니다.
        </UI.Typography>
      </FilterSection>
      <Section>
        {!loading && <ViolationRsvListTable violationRsvList={violationRsvList} />}
        {loading && <Skeleton style={{ width: "100%", height: 646, borderRadius: 12 }} />}
        {!loading && totalPage > 0 && (
          <UI.Pagination
            pageCount={totalPage}
            currentPage={page}
            onChange={(page) => {
              setPage(page);
            }}
          />
        )}
      </Section>
    </Container>
  );
};

export default React.memo(ViolationReservations);
